import { faCommentsAlt } from '@fortawesome/pro-duotone-svg-icons';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User, UserId } from 'api/models';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import {
  Container,
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { Empty, Loaded, Loading } from 'core/state';
import { useGetAllActiveUsersMap } from 'hooks/use-idb';
import { fullName } from 'utils/full-name';

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    padding: var(--spacing-08) var(--spacing-06) var(--spacing-05);
  }
  ${Container} {
    width: auto;
    max-width: max-content;
  }

  ${EmptyState.Image} {
    width: 96px;
  }
`;

interface Props {
  userId: UserId;
  approverIds: Empty | Loading | Loaded<number[]>;
}

export const TimesheetsEmptyState: FC<Props> = ({
  userId: [, userId],
  approverIds,
}) => {
  const users = useGetAllActiveUsersMap();
  const approvers = useMemo(
    () => (approverIds[1] ?? []).map((x) => users[x]) as User[],
    [approverIds, users],
  );

  const user = users[userId]?.firstName;

  return (
    <EmptyStateContainer $transparent>
      <EmptyState
        image={
          <AvatarGroup maxCount={3}>
            {approvers?.length
              ? approvers.map((x) => (
                  <Avatar
                    key={x?.userId}
                    name={fullName(x)}
                    src={x?.photoUrl}
                  />
                ))
              : null}
          </AvatarGroup>
        }
        title={
          <FormattedMessage
            id="timesheets.conversations.title"
            values={{ user }}
          />
        }
        message={<FormattedMessage id="timesheets.conversations.message" />}
      />
    </EmptyStateContainer>
  );
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  opacity: 0.4;
  padding: var(--spacing-06);
`;

export const ConversationsIcon: FC = () => (
  <Wrapper>
    <EmptyStateIcon icon={faCommentsAlt} size="3x" />
  </Wrapper>
);
